import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { MainLayout } from 'views/layouts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useNavigate, useLocation } from 'react-router-dom'
import { Container, DashboardContainer, Row, BlueContainer } from './styled'
import { H2, H4, P } from 'views/components/UI/Text'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import WhiteContainer from 'views/components/common/WhiteContainer'
import BluesiteTable from 'views/components/UI/BluesiteTable'
import SearchBar from 'views/components/Searchbar/search-bar'
import { useNotification } from 'lib/context/notification.context'
import { ActionViewDownloadReportProcessCreated } from 'views/components/UI/BluesiteTable/Actions/GridActions'
import { platformAdminRoutes } from 'router/routes'
import { userSelector } from 'ducks/user'
import { useFetchGetResumeProcess, useFetchGetUsersByProcess } from 'hooks/process/useGetProcess/useGetProcess'
import { useGetReports } from 'hooks/process/useGetReports/useGetReports'
import sendReportToUser from 'services/reports/sendReportResultToUserEvaluated/sendReportToUser'
import AddUserIcon from 'assets/icons/AddUserIcon'
import { DEFAULT_LIMIT } from 'constants/global.constants'
import { UniqueUserIds, FilterGetUsersByProcess } from '../store'
import { HEADERS_USERS_WITH_TESTS_COMPLETED } from '../../Mocks/mocks-platform-admin'
import { fetchUpdateUserTestAction } from '../../constants/fetch_process'
import { PrimaryButton, StrokedButton } from 'views/components/UI/Buttons'
import Modal from 'views/components/UI/Modal'
import ItemDashboard from './components/ItemDashboard'
import { getLogoUrl } from '../../constants/fetch_process'
import { sendTestsToUsers } from '../../services'
import { downloadBetesaProcessFollowUpsExcel } from 'services/results/betesa-results'

type ParticipationMapType = Record<number, boolean>

const formatDateValue = (...dateCandidates: Array<string | null | undefined>) => {
  const validDate = dateCandidates.find((date) => Boolean(date))

  return validDate ? dayjs.utc(validDate).format('DD/MM/YYYY') : null
}

const DetailProcessDelivered = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const processId = location.state?.id as number
  const { companyId } = useSelector(userSelector)
  const { getError, getSuccess, getWarning } = useNotification()
  const [participation, setParticipation] = useState<boolean>(false)
  const [userIds, setUserIds] = useState<Set<number>>(new Set())
  const [search, setSearch] = useState('')
  const [filterGetUsers, setFilterGetUsers] = useState<FilterGetUsersByProcess>({
    limit: DEFAULT_LIMIT,
    complete: true,
    evaluatedOnly: true,
    viewUsersWithoutTests: false,
  })
  const [participationMap, setParticipationMap] = useState<ParticipationMapType>({})
  const [showFollowUpModal, setShowFollowUpModal] = useState(false)
  const [followUpMode, setFollowUpMode] = useState<'all' | 'selected'>('all')
  const [followUpUserIds, setFollowUpUserIds] = useState<Set<number>>(new Set())
  const [followUpSearch, setFollowUpSearch] = useState('')
  const [sendingFollowUp, setSendingFollowUp] = useState(false)
  const [compareFollowUpWithIdeal, setCompareFollowUpWithIdeal] = useState(false)
  const [downloadingFollowUpsExcel, setDownloadingFollowUpsExcel] = useState(false)
  const { listProcessUsersResume, listProcessTestResume, fetchProcessResume } = useFetchGetResumeProcess(processId)
  const { listUsersByProcess, totalPages, fetchUsers, loadingUsersByProcess } = useFetchGetUsersByProcess(
    processId,
    filterGetUsers
  )
  const { loadingGetReports, reportsResult, handleGetReports } = useGetReports(processId)
  const {
    name,
    total_users: totalUsers,
    total_users_completed: totalUsersCompleted,
    total_users_uncompleted: totalUsersUncompleted,
  } = listProcessUsersResume
  const { test_360: isTest360, evaluators_count: evaluatorsCount, hasBetesaIdealProfile } = listProcessTestResume
  const uniqueUserIds: UniqueUserIds = {}
  dayjs.extend(utc)

  const dataAllUsers = listUsersByProcess.reduce((accumulator: any[], user: any) => {
    if (!uniqueUserIds[user.userId]) {
      uniqueUserIds[user.userId] = true

      accumulator.push({
        id: user.userId,
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        document: user.document,
        dateSend: formatDateValue(user.send_date, user.shipping_date) || 'Sin envío',
        answer_date: formatDateValue(user.answer_date, user.reply_date, user.date_completed) || 'Sin respuesta',
      })
    }

    return accumulator
  }, [])

  useEffect(() => {
    if (reportsResult.status === 'success') {
      const { data } = reportsResult
      const dateZip = new Date()
      const dia = dateZip.getDate()
      const mes = dateZip.getMonth() + 1
      const año = dateZip.getFullYear()
      const url = window.URL.createObjectURL(data)

      const a = document.createElement('a')

      a.href = url

      a.download = `${name}_${dia}_${mes}_${año}.zip`

      a.click()

      window.URL.revokeObjectURL(url)

      setUserIds(new Set())
    }
  }, [name, reportsResult])

  useEffect(() => {
    fetchProcessResume()
    fetchUsers()
  }, [fetchProcessResume, fetchUsers])

  useEffect(() => {
    if (reportsResult.status === 'error') {
      getError(reportsResult.error)
    }
  }, [reportsResult, getError, getSuccess])

  const handleSetFilterGetUsers = (searchTerm: string) => {
    setFilterGetUsers({ ...filterGetUsers, search: searchTerm, page: 1 })
  }

  const handlePagination = (page: number) => {
    setFilterGetUsers({ ...filterGetUsers, limit: DEFAULT_LIMIT, page })
  }

  const handleDownloadSingleReport = (userId: number) => {
    const newSetId = new Set([userId])

    handleGetReports({ userIds: newSetId })
  }

  // const handleDownloadMultipleReports = () => {
  //   const newSetIds = new Set(userIds)
  //   handleGetReports({ userIds: newSetIds })
  // }

  const handleSendReportToMultipleUsers = async (selectedUsersIds: number[]) => {
    const userTestIds: number[] = []
    const usersIdData: number[] = []
    const allUsersIds = Array.from(selectedUsersIds)
    try {
      if (!allUsersIds.length) {
        getWarning('Debes seleccionar los usuarios que quieres autorizar')
        return
      } else {
        setParticipation(true)
        userIds.forEach((user: number) => {
          const userProcesses = listUsersByProcess.filter(
            (obj: { userId: number; processId: number }) => obj.userId === user
          )
          userProcesses.forEach((userTestId: any) => {
            userTestIds.push(userTestId.user_test_id)
          })
          usersIdData.push(user)
        })
        fetchUpdateUserTestAction(userTestIds, true)
        await sendReportToUser({ companyId: companyId, users: usersIdData })
        getSuccess('Se ha concedido el acceso a los usuarios seleccionados')
        const updatedParticipationMap: ParticipationMapType = { ...participationMap }
        selectedUsersIds.forEach((userId) => {
          updatedParticipationMap[userId] = true
        })
        setParticipationMap(updatedParticipationMap)
        setParticipation(false)
      }
    } catch (error: any) {
      getError(`Error al autorizar el acceso a los reportes ${error.message}`)
    }
  }

  const handleWatchSettings = () => {
    navigate(platformAdminRoutes.watchSettings, { state: { processId, name } })
  }

  const handleViewAnalytics = () => {
    navigate(platformAdminRoutes.Analitycs, { state: { processId, processName: name } })
  }

  const filteredUsers = listUsersByProcess.filter((user: any) => !(user.participant_report || user.access_report))
  const followUpUsers = dataAllUsers.filter((user: any) =>
    `${user.name} ${user.email} ${user.document}`.toLowerCase().includes(followUpSearch.toLowerCase())
  )
  const isButtonDisabled =
    listUsersByProcess.length === 0 ||
    !filteredUsers ||
    filteredUsers.length < 1 ||
    participation ||
    Array.from(userIds).some((userId) => participationMap[userId])

  const toggleFollowUpUser = (userId: number) => {
    const nextUserIds = new Set(followUpUserIds)
    if (nextUserIds.has(userId)) nextUserIds.delete(userId)
    else nextUserIds.add(userId)
    setFollowUpUserIds(nextUserIds)
  }

  const handleSendFollowUps = async () => {
    const selectedIds: number[] =
      followUpMode === 'all' ? dataAllUsers.map((user: any) => user.id) : Array.from(followUpUserIds)

    if (!selectedIds.length) {
      getWarning('Debes seleccionar al menos un usuario para enviar Follow-Ups')
      return
    }

    try {
      setSendingFollowUp(true)
      const logoResponse = await getLogoUrl(processId)
      const response = await sendTestsToUsers({
        process_id: processId,
        company_id: companyId,
        type: 'follow_up',
        compare_with_ideal_profile: compareFollowUpWithIdeal,
        users_data: selectedIds.map((userId) => ({ user_id: userId })),
        email_template: {
          date: dayjs.utc().add(7, 'day').format('DD/MM/YYYY'),
          logo: logoResponse?.cobranding?.logo,
        },
      })

      if (response.status === 'OK') {
        const sentCount = Array.isArray(response.users) ? response.users.length : 0
        const skippedCount = Array.isArray(response.skipped) ? response.skipped.length : 0
        getSuccess(
          `Se enviaron ${sentCount} Follow-Up${sentCount === 1 ? '' : 's'}${
            skippedCount
              ? `; ${skippedCount} usuario${skippedCount === 1 ? '' : 's'} omitido${skippedCount === 1 ? '' : 's'}`
              : ''
          }`
        )
        setShowFollowUpModal(false)
        setFollowUpUserIds(new Set())
        setCompareFollowUpWithIdeal(false)
      } else {
        getError(response.message || 'No fue posible enviar los Follow-Ups')
      }
    } catch (error: any) {
      getError(error.message || 'No fue posible enviar los Follow-Ups')
    } finally {
      setSendingFollowUp(false)
    }
  }

  const handleDownloadFollowUpsExcel = async () => {
    try {
      setDownloadingFollowUpsExcel(true)
      const data = await downloadBetesaProcessFollowUpsExcel(processId)
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.download = `${name || 'Proceso'}_Follow-Ups_Betesa.xlsx`
      link.click()
      window.URL.revokeObjectURL(url)
    } catch (error: any) {
      getError(error.message || 'No fue posible generar el Excel de Follow-Ups')
    } finally {
      setDownloadingFollowUpsExcel(false)
    }
  }

  return (
    <MainLayout>
      {loadingGetReports && (
        <ModalLoader
          style={{ backgroundColor: 'white', padding: '30px', borderRadius: '30px' }}
          text="Generando reportes..."
          isLoading={loadingGetReports}
        />
      )}
      <Container>
        <Row>
          <H2>{name || ''}</H2>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <StrokedButton onClick={() => setShowFollowUpModal(true)} type="button" disabled={!dataAllUsers.length}>
              Enviar Follow-Ups
            </StrokedButton>
            <StrokedButton onClick={handleDownloadFollowUpsExcel} type="button" disabled={downloadingFollowUpsExcel}>
              {downloadingFollowUpsExcel ? 'Generando Excel...' : 'Descargar Excel de Follow-Ups'}
            </StrokedButton>
            <StrokedButton onClick={handleWatchSettings} type="button">
              Ver parametrización
            </StrokedButton>
            <PrimaryButton onClick={handleViewAnalytics} type="button">
              Ver analíticas
            </PrimaryButton>
          </div>
        </Row>
        <DashboardContainer>
          <WhiteContainer
            padding="28px 8px 28px 8px"
            width="28%"
            height="90px"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <ItemDashboard text="Total de usuarios en el proceso" quantity={totalUsers} />
            <ItemDashboard showDivider text="Usuarios con pruebas completadas" quantity={totalUsersCompleted} />
          </WhiteContainer>
          <BlueContainer padding="28px 8px 28px 8px" width="185px" height="90px" margin="0px 16px">
            <ItemDashboard
              color="#FFF"
              text="Evaluados con pruebas sin completar"
              quantity={totalUsersUncompleted}
              onClick={() => navigate(platformAdminRoutes.UsersWithIncompleteTests, { state: listProcessUsersResume })}
            />
          </BlueContainer>
          {isTest360 && (
            <BlueContainer padding="28px 8px 28px 8px" width="185px" height="90px" margin="0px 0px" alignItems="center">
              <ItemDashboard
                color="#FFF"
                text="Evaluadores"
                quantity={evaluatorsCount}
                onClick={() => navigate(platformAdminRoutes.EvaluatorsTest360, { state: listProcessUsersResume })}
              />
            </BlueContainer>
          )}
        </DashboardContainer>
        <Row alignItems="flex-end">
          <H4>Lista de usuarios con pruebas completadas</H4>
            <SearchBar
            label="Buscar por nombre, correo, documento"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              handleSetFilterGetUsers(e.target.value)
            }}
            onClick={() => handleSetFilterGetUsers(search)}
          />
        </Row>
        <Row justifyContent="center" alignItems="space-between" margin="31px 0px">
          {/* <Row justifyContent="flex-start" alignItems="center">
            <StrokedButton
              style={{ marginBottom: '0', display: 'flex', flexDirection: 'row', alignItems: 'center' }}
              disabled={!(userIds.size > 0)}
              onClick={handleDownloadMultipleReports}
            >
              <DownloadIcon style={{ marginRight: '10px', color: !(userIds.size > 0) ? '#C2C2C2' : '#2D749C' }} />
              {dataAllUsers.length === userIds.size ? (
                <div>Descargar todos los reportes</div>
              ) : (
                <div>Descargar reportes</div>
              )}
            </StrokedButton>
          </Row> */}
          <Row justifyContent="flex-end" margin="30px 0px 10px 0px" alignItems="start">
            <AddUserIcon disabled={isButtonDisabled} />
            <P
              disabled={isButtonDisabled}
              onClick={() => {
                if (!isButtonDisabled) {
                  handleSendReportToMultipleUsers(Array.from(userIds))
                } else {
                  getWarning('Los usuarios tienen configurada la descarga de reporte automática')
                }
              }}
              style={{ cursor: 'pointer' }}
              margin="0px 6px"
              color="#2D749C"
              fontWeight={700}
              fontSize="14px"
            >
              {dataAllUsers.length === userIds.size ? (
                <>Autorizar acceso a todos los usuarios</>
              ) : (
                <>Autorizar acceso a usuarios</>
              )}
            </P>
          </Row>
        </Row>

        <BluesiteTable
          loading={loadingUsersByProcess}
          headers={HEADERS_USERS_WITH_TESTS_COMPLETED}
          data={dataAllUsers}
          searchBar={false}
          searchLabel=""
          totalPages={totalPages}
          currentPage={filterGetUsers.page ? filterGetUsers.page : 1}
          handlePagination={handlePagination}
          ActionComponent={ActionViewDownloadReportProcessCreated}
          checks={true}
          selectedItems={userIds}
          setSelectedItems={(e) => setUserIds(e)}
          extraDataAction={{
            listIdUsers: userIds,
            onDownloadReport: (e: number) => handleDownloadSingleReport(e),
            processId,
          }}
        />
      </Container>
      <Modal
        show={showFollowUpModal}
        setShow={setShowFollowUpModal}
        styleContent={{ width: '560px', maxWidth: 'calc(100% - 32px)', maxHeight: '80vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <H4>Enviar Follow-Ups de Betesa</H4>
          <P margin="0">
            Se recomienda realizar la nueva medición después de seis meses. Esta recomendación no bloquea el envío.
          </P>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="radio"
              name="follow-up-mode"
              checked={followUpMode === 'all'}
              onChange={() => setFollowUpMode('all')}
            />
            Enviar a todos los usuarios con Betesa completado ({dataAllUsers.length})
          </label>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="radio"
              name="follow-up-mode"
              checked={followUpMode === 'selected'}
              onChange={() => setFollowUpMode('selected')}
            />
            Seleccionar usuarios
          </label>
          {followUpMode === 'selected' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input
                type="search"
                placeholder="Buscar por nombre, correo o documento"
                value={followUpSearch}
                onChange={(event) => setFollowUpSearch(event.target.value)}
                style={{ padding: '10px', border: '1px solid #C2C2C2', borderRadius: '6px' }}
              />
              <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #E1E1E1', borderRadius: '6px' }}>
                {followUpUsers.map((user: any) => (
                  <label
                    key={user.id}
                    style={{
                      display: 'flex',
                      gap: '10px',
                      alignItems: 'center',
                      padding: '10px 12px',
                      borderBottom: '1px solid #F0F0F0',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={followUpUserIds.has(user.id)}
                      onChange={() => toggleFollowUpUser(user.id)}
                    />
                    <span>
                      {user.name} · {user.email}
                    </span>
                  </label>
                ))}
                {!followUpUsers.length && <P margin="12px">No se encontraron usuarios.</P>}
              </div>
              <P margin="0">Usuarios seleccionados: {followUpUserIds.size}</P>
            </div>
          )}
          {hasBetesaIdealProfile && (
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={compareFollowUpWithIdeal}
                onChange={(event) => setCompareFollowUpWithIdeal(event.target.checked)}
              />
              Contrastar los resultados del Follow-Up con el perfil ideal del proceso
            </label>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <StrokedButton onClick={() => setShowFollowUpModal(false)} disabled={sendingFollowUp}>
              Cancelar
            </StrokedButton>
            <PrimaryButton onClick={handleSendFollowUps} disabled={sendingFollowUp}>
              {sendingFollowUp ? 'Enviando...' : 'Enviar Follow-Ups'}
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </MainLayout>
  )
}

export default DetailProcessDelivered
